/*
 * Planigo
 * Auteur  : Stéphane Raverdy
 * Société : SCIAD 2026
 */
import { useMemo, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { DeviceMobile, DeviceTablet, Desktop, ArrowClockwise, ArrowSquareOut, Wrench, Info } from '@phosphor-icons/react';
import { useAuth } from '../contexts/AuthContext';

const DEVICES = {
  mobile: { label: 'Mobile', width: 390, height: 780, icon: DeviceMobile },
  tablet: { label: 'Tablette portrait', width: 820, height: 1024, icon: DeviceTablet },
  tablet_landscape: { label: 'Tablette paysage', width: 1180, height: 760, icon: DeviceTablet },
  desktop: { label: 'Desktop', width: 1366, height: 820, icon: Desktop },
};

const SCREENS = [
  ['/', 'Tableau de bord'], ['/projects', 'Projets'], ['/tasks', 'Tâches & GANTT'],
  ['/interventions', 'Interventions'], ['/assets', 'Parc matériel'], ['/gmao', 'Cockpit GMAO'],
  ['/store', 'Magasin'], ['/inventory', 'Inventaires'], ['/maintenance-planning', 'Planning GMAO'],
];

export default function TechnicalPage() {
  const { user } = useAuth();
  const [device, setDevice] = useState('mobile');
  const [screen, setScreen] = useState('/');
  const [refreshKey, setRefreshKey] = useState(0);
  const cfg = DEVICES[device];
  const DeviceIcon = cfg.icon;
  const simulatorUrl = useMemo(() => {
    const url = new URL(screen, window.location.origin);
    url.searchParams.set('planigo_simulator', '1');
    url.searchParams.set('device', device.startsWith('tablet') ? 'tablet' : device);
    return url.toString();
  }, [screen, device]);



  return <div className="space-y-4" data-testid="technical-page">
    <div className="rounded-2xl border bg-gradient-to-r from-slate-950 via-slate-900 to-blue-950 text-white p-5 shadow-sm">
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
        <div><div className="flex items-center gap-2 text-blue-200 text-xs font-semibold uppercase tracking-wider"><Wrench size={16}/>Technique</div><h1 className="text-2xl font-black mt-1">Simulateur appareils</h1><p className="text-sm text-slate-300 mt-1 max-w-3xl">Simulez les écrans Planigo sur mobile, tablette et desktop sans modifier les données métier.</p></div>
        <Badge className="bg-white/10 text-white border border-white/20">{user?.name || user?.email}</Badge>
      </div>
    </div>


    <Card className="card-surface">
      <CardHeader className="pb-2"><CardTitle className="text-sm">Configuration de la simulation</CardTitle></CardHeader>
      <CardContent className="grid lg:grid-cols-[220px_280px_1fr] gap-3 items-end">
        <div><div className="text-xs font-semibold mb-1">Appareil</div><Select value={device} onValueChange={setDevice}><SelectTrigger><SelectValue/></SelectTrigger><SelectContent>{Object.entries(DEVICES).map(([key,row])=><SelectItem key={key} value={key}>{row.label} · {row.width}px</SelectItem>)}</SelectContent></Select></div>
        <div><div className="text-xs font-semibold mb-1">Écran Planigo</div><Select value={screen} onValueChange={setScreen}><SelectTrigger><SelectValue/></SelectTrigger><SelectContent>{SCREENS.map(([path,label])=><SelectItem key={path} value={path}>{label}</SelectItem>)}</SelectContent></Select></div>
        <div className="flex flex-wrap gap-2 lg:justify-end"><Button variant="outline" onClick={()=>setRefreshKey(x=>x+1)}><ArrowClockwise size={15} className="mr-1"/>Recharger</Button><Button variant="outline" onClick={()=>window.open(simulatorUrl,'_blank','noopener,noreferrer')}><ArrowSquareOut size={15} className="mr-1"/>Ouvrir</Button></div>
      </CardContent>
    </Card>

    <div className="rounded-xl border bg-blue-50/60 text-blue-900 px-4 py-3 flex gap-2 text-xs"><Info size={17} className="shrink-0 mt-0.5"/><span>Le simulateur n'accorde aucun droit supplémentaire. Les réglages <b>Menus Desktop / Mobile / Tablette</b> du rôle limitent la présentation disponible sur chaque terminal.</span></div>
    <div className="overflow-auto rounded-2xl border bg-slate-100/70 p-5 min-h-[640px]"><div className="mx-auto transition-all duration-200" style={{width:`${cfg.width}px`, maxWidth:'100%'}}><div className="flex items-center justify-between mb-2 px-1 text-xs text-slate-500"><span className="flex items-center gap-1"><DeviceIcon size={15}/>{cfg.label}</span><span>{cfg.width} × {cfg.height}</span></div><div className="bg-slate-950 rounded-[26px] p-[8px] shadow-2xl border border-slate-700"><div className="bg-white overflow-hidden rounded-[19px]" style={{height:`${cfg.height}px`, maxHeight:'72vh'}}><iframe key={`${refreshKey}-${device}-${screen}`} title="Simulation Planigo" src={simulatorUrl} className="w-full h-full border-0 bg-white" /></div></div></div></div>
  </div>;
}
